/* eslint-disable eol-last */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import getValidationSchema from "./getValidationSchema";
import {
  addShares,
  editShares,
} from "state/slices/tables/sharesAllocationTable";
import { saveManualAllocation } from "state/slices/manualShareAllocation";
import { getTakenShares, getUpdatedFormData, roundNumber } from "utils";
import Input from "components/lib/Shared/Input";
import Button from "components/lib/Shared/Button";
import DatePickerField from "components/lib/Shared/DatePickerField";
import { useToast } from "hooks/useToast";
import { getFullName } from "utils";
import { format } from "date-fns";

const formFields = [
  "classOfShares",
  "allottedShares",
  "paidUpShares",
  "sharesValue",
  "sharePrice",
  "sharePremium",
];

const ShareAllocationForm = ({
  formikHelpers,
  nominalShareCapital,
  isInManualUpdate,
  getManualChangeByClass,
  computeRemainingForChange,
  takenShares,
  alreadyTakenClassOfShares,
  editableShareAlloc,
  setValidationSchema,
  isInChangesList,
  manualClassOptions,
  setSelectedClassOfShare,
  selectedClassOfShare,
  roundNumber,
  handleCancel,
  remainderAmounts,
}) => {
  const { setFieldValue, values, handleSubmit } = formikHelpers;

  const selectedClassOfShares = nominalShareCapital.find(
    ({ sharesCategory }) => sharesCategory === values.classOfShares
  );

  const selectedManualChange = isInManualUpdate
    ? getManualChangeByClass(values.classOfShares)
    : null;

  const manualSharesOnOffer = Number(selectedManualChange?.remainder || 0);
  const manualRemaining = computeRemainingForChange(selectedManualChange);

  // Calculate available shares including remainder amounts for changes list
  const getAvailableSharesForChangesList = () => {
    if (!isInChangesList || !remainderAmounts || !values?.classOfShares) {
      return (
        (selectedClassOfShares?.sharesNumber ?? 0) -
        (takenShares?.[values?.classOfShares] ?? 0)
      );
    }

    // Find remainder for the selected class of shares
    const remainderForClass = remainderAmounts.find(
      (remainder) => remainder.classOfShares === values.classOfShares
    );

    const remainderAmount = remainderForClass?.remainder || 0;
    const baseShares =
      (selectedClassOfShares?.sharesNumber ?? 0) -
      (takenShares?.[values?.classOfShares] ?? 0);

    // Add remainder amount to base shares for changes list
    return baseShares + remainderAmount;
  };

  const availableShares = isInManualUpdate
    ? manualSharesOnOffer
    : getAvailableSharesForChangesList();

  useEffect(() => {
    const syncShareValues = () => {
      if (isInManualUpdate) {
        if (!selectedManualChange) {
          return;
        }

        const manualSharesValue =
          Number(selectedManualChange?.offerPricePerShare || 0) -
          Number(selectedManualChange?.premiumPerShare || 0);
        setFieldValue("sharesValue", manualSharesValue);

        setFieldValue(
          "sharePremium",
          roundNumber(
            values?.sharePrice ? values?.sharePrice - manualSharesValue : 0
          )
        );
      } else {
        if (!selectedClassOfShares) {
          return;
        }

        const baseSharesValue = selectedClassOfShares?.sharesValue ?? 0;
        setFieldValue("sharesValue", baseSharesValue);
        setFieldValue(
          "sharePremium",
          roundNumber(
            values?.sharePrice ? values?.sharePrice - baseSharesValue : 0
          )
        );
      }
    };

    syncShareValues();
  }, [
    isInManualUpdate,
    roundNumber,
    selectedClassOfShares,
    selectedManualChange,
    setFieldValue,
    values?.sharePrice,
  ]);

  useEffect(() => {
    const sharesValueForSchema = isInManualUpdate
      ? Number(
          selectedManualChange
            ? Number(selectedManualChange?.offerPricePerShare || 0) -
                Number(selectedManualChange?.premiumPerShare || 0)
            : 0
        )
      : selectedClassOfShares?.sharesValue ?? 0;

    let availableForSchema;
    if (isInChangesList) {
      // For changes list, include remainder amounts in validation schema
      const baseAvailable =
        (editableShareAlloc?.allottedShares || 0) +
        (manualRemaining || selectedManualChange?.remainder || 0);

      // Add remainder amount for the selected class if available
      if (remainderAmounts && values?.classOfShares) {
        const remainderForClass = remainderAmounts.find(
          (remainder) => remainder.classOfShares === values.classOfShares
        );
        const remainderAmount = remainderForClass?.remainder || 0;
        availableForSchema = baseAvailable + remainderAmount;
      } else {
        availableForSchema = baseAvailable;
      }
    } else if (isInManualUpdate) {
      availableForSchema = manualRemaining || manualSharesOnOffer;
    } else {
      availableForSchema = availableShares;
    }

    setValidationSchema(
      getValidationSchema({
        availableShares: availableForSchema,
        allottedShares: values?.allottedShares ?? 0,
        sharesValue: sharesValueForSchema,
      }).schema
    );
  }, [
    availableShares,
    editableShareAlloc?.allottedShares,
    isInChangesList,
    isInManualUpdate,
    manualRemaining,
    manualSharesOnOffer,
    remainderAmounts,
    selectedClassOfShares?.sharesValue,
    selectedManualChange,
    selectedManualChange?.offerPricePerShare,
    selectedManualChange?.premiumPerShare,
    selectedManualChange?.remainder,
    setValidationSchema,
    values?.allottedShares,
    values?.classOfShares,
  ]);

  const handleClassOfShareChange = (option) => {
    setFieldValue("classOfShares", option.value);
    setSelectedClassOfShare(option);
  };

  const getAvailableSharesOptions = (taken) => {
    if (isInManualUpdate) {
      return manualClassOptions;
    }
    const availableOptions = [];
    nominalShareCapital.forEach((nominalShare) => {
      if (
        (nominalShare?.sharesNumber ?? 0) -
          (taken?.[nominalShare?.sharesCategory] ?? 0) >
        0
      ) {
        availableOptions.push({
          label: nominalShare.sharesCategory,
          value: nominalShare.sharesCategory,
        });
      }
    });
    return availableOptions.filter(
      (option) =>
        !alreadyTakenClassOfShares.includes(option.value) ||
        (editableShareAlloc &&
          option.value === editableShareAlloc?.classOfShares)
    );
  };

  return (
    <Form>
      <div className="space-y-6">
        <h3 className="mb-2 text-lg font-medium leading-6 text-gray-900">
          Manual Share Allocation
        </h3>
        <Field
          label="Class Of Shares"
          value={selectedClassOfShare}
          name="classOfShares"
          component={ReactSelectWithFormik}
          options={getAvailableSharesOptions(takenShares)}
          errorPath="classOfShares"
          placeholder="Select class of shares"
          onChange={handleClassOfShareChange}
        />

        <hr className="my-2" />

        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-3">
            <Field
              component={Input}
              label="Number of shares allotted"
              type="number"
              name="allottedShares"
              placeholder="Add number of shares. Eg 100"
              disabled={
                isInManualUpdate
                  ? !selectedManualChange
                  : !selectedClassOfShares?.sharesCategory
              }
            />
            <h6 className="mt-1">
              {isInChangesList ? (
                <>
                  Shares available to allot:{" "}
                  {roundNumber(
                    (() => {
                      const baseAvailable =
                        (editableShareAlloc?.allottedShares || 0) +
                        (manualRemaining ||
                          selectedManualChange?.remainder ||
                          0);

                      // Add remainder amount for the selected class if available
                      let totalAvailable = baseAvailable;
                      if (remainderAmounts && values?.classOfShares) {
                        const remainderForClass = remainderAmounts.find(
                          (remainder) =>
                            remainder.classOfShares === values.classOfShares
                        );
                        const remainderAmount =
                          remainderForClass?.remainder || 0;
                        totalAvailable = baseAvailable + remainderAmount;
                      }

                      return totalAvailable - (values?.allottedShares || 0);
                    })()
                  )}{" "}
                </>
              ) : isInManualUpdate ? (
                <>
                  Shares available to allot:{" "}
                  {roundNumber(manualRemaining - (values?.allottedShares ?? 0))}
                </>
              ) : (
                <>
                  Shares available to allot:{" "}
                  {roundNumber(availableShares - (values?.allottedShares ?? 0))}
                </>
              )}
            </h6>
          </div>

          <div className="col-span-6 sm:col-span-3">
            <Field
              component={Input}
              label="Number of shares fully paid up"
              type="number"
              value={values?.paidUpShares?.toString()}
              name="paidUpShares"
              placeholder="Add number of shares. Eg 100"
              disabled={
                isInManualUpdate
                  ? !selectedManualChange
                  : !selectedClassOfShares?.sharesCategory
              }
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <Field
              component={Input}
              label="Amount to be paid per share"
              type="number"
              name="sharePrice"
              placeholder="Add amount no less than 1"
              disabled={
                isInManualUpdate
                  ? !selectedManualChange
                  : !selectedClassOfShares?.sharesValue
              }
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <Field
              component={Input}
              label="Premium in paid share"
              type="number"
              name="sharePremium"
              placeholder="0"
              readOnly
            />
          </div>

          {isInManualUpdate && (
            <div className="col-span-6 sm:col-span-3">
              <Field
                component={DatePickerField}
                label="Effective Date of Change"
                name="effectiveDate"
                placeholder="Select effective date"
                maxDate={new Date()}
                selected={
                  values.effectiveDate ? new Date(values.effectiveDate) : null
                }
                form={{
                  setFieldValue,
                  errors: {},
                  touched: {},
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-2 space-x-2">
        <Button
          onClick={handleCancel}
          className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {editableShareAlloc ? "Submit" : "Submit"}
        </Button>
      </div>
    </Form>
  );
};

const ShareAllocationSelection = (props) => {
  const {
    editableShareAlloc,
    setEditableShareAlloc,
    setAllocateSharesBtn,
    isInManualUpdate,
    transactions,
    row,
    onCloseForm,
    onSubmissionComplete,
    remainderAmounts,
    selectedClassOfShare,
    setSelectedClassOfShare,
    alreadyTakenClassOfShares,
    isInChangesList, // New prop to indicate changes list mode
    currentOfficialId,
  } = props;

  const officialFormContext = useFormikContext();
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const { companyId } = useParams();

  const companyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );

  const [validationSchema, setValidationSchema] = useState(null);

  const takenShares = getTakenShares(
    companyOfficials.filter((co) => co._id !== props.currentOfficialId),
    editableShareAlloc?._id
  );

  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );

  const manualTx = useMemo(() => {
    if (!transactions) return null;
    if (Array.isArray(transactions)) {
      return (
        transactions.find((t) => t?.transactionType === "MANUAL") ||
        transactions[0] ||
        null
      );
    }
    return transactions;
  }, [transactions]);

  const shareCapitalChanges = manualTx?.shareCapitalChanges || [];

  const manualClassOptions = useMemo(
    () =>
      shareCapitalChanges.map((c) => ({
        label: c.classOfShares,
        value: c.classOfShares,
      })),
    [shareCapitalChanges]
  );

  const getManualChangeByClass = (cls) =>
    shareCapitalChanges.find((c) => c.classOfShares === cls) || null;

  const computeRemainingForChange = (change) => {
    if (!change) return 0;

    // Check form data first for accurate remaining calculation
    const formShares = officialFormContext?.values?.sharesAllotted || [];
    if (formShares.length > 0) {
      const allocatedInForm = formShares
        .filter((share) => share.classOfShares === change.classOfShares)
        .reduce((sum, share) => sum + Number(share.allottedShares || 0), 0);
      return Math.max(0, Number(change.sharesOnOffer || 0) - allocatedInForm);
    }

    // Fallback to transaction remainder or allocation calculation
    if (typeof change.remainder === "number") return change.remainder;

    const allocatedTotal = (change.allocations || []).reduce(
      (sum, a) => sum + Number(a.allocatedShares || 0),
      0
    );
    return Math.max(0, Number(change.sharesOnOffer || 0) - allocatedTotal);
  };

  // Function to save allotment data to Redux
  const saveAllotmentToRedux = (allotmentData) => {
    const officialId = row?.original?._id;
    dispatch(
      saveManualAllocation({
        companyId,
        officialId,
        transactionId: manualTx?._id,
        allocationData: allotmentData,
      })
    );
  };

  const handleCancel = () => {
    // Cancel should not trigger any BO updates, just reset UI state
    setAllocateSharesBtn(true); // Show table instead of form
    setEditableShareAlloc(null);
    onCloseForm?.();
  };

  useEffect(() => {
    setValidationSchema(getValidationSchema({}).schema);
    setSelectedClassOfShare("");
  }, [setSelectedClassOfShare]);

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          classOfShares: "",
          allottedShares: "",
          paidUpShares: "",
          sharesValue: "",
          sharePrice: "",
          sharePremium: "",
          effectiveDate: (() => {
            if (!isInManualUpdate) return "";
            if (shareCapitalChanges.length > 0) {
              const firstChange = shareCapitalChanges[0];
              if (firstChange?.dateOfOffer) {
                try {
                  const dateOfOffer = new Date(firstChange.dateOfOffer);

                  if (!isNaN(dateOfOffer.getTime())) {
                    const formattedDate = format(dateOfOffer, "yyyy-MM-dd");
                    return formattedDate;
                  }
                } catch (error) {}
              }
            }
            return "";
          })(),
          ...editableShareAlloc,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          // Special handling for changes list mode
          if (isInChangesList) {
            try {
              setSubmitting(true);

              // Process the form values
              const selectedClassOfShares = nominalShareCapital.find(
                ({ sharesCategory }) => sharesCategory === values.classOfShares
              );
              const nominalValue = values.allottedShares * values.sharesValue;
              const payload = {
                ...values,
                nominalValue,
                votingRights:
                  values.allottedShares *
                  (selectedClassOfShares?.votesPerShare || 0),
              };

              // Update the form data
              if (editableShareAlloc) dispatch(editShares(payload));
              else dispatch(addShares(payload));

              // Notify parent component
              resetForm();
              setAllocateSharesBtn(true); // Changed from payload to true to show table instead of form
              setEditableShareAlloc(null);

              if (onSubmissionComplete) {
                onSubmissionComplete(payload);
              }

              toast("success", "Share allocation updated successfully.");
            } catch (err) {
              toast(
                "error",
                err?.message || "Failed to update allocation. Please try again."
              );
            } finally {
              setSubmitting(false);
            }
            return;
          }

          if (isInManualUpdate) {
            try {
              setSubmitting(true);

              if (!manualTx?._id) {
                toast("error", "No manual transaction found to update.");
                return;
              }

              const selectedChange = getManualChangeByClass(
                values.classOfShares
              );
              if (!selectedChange) {
                toast(
                  "error",
                  "Could not find a matching share capital change for the selected class."
                );
                return;
              }

              const onOffer = Number(selectedChange.sharesOnOffer || 0);
              const remainingBefore = computeRemainingForChange(selectedChange);

              const toAllocate = Number(values.allottedShares || 0);
              if (!toAllocate || toAllocate < 1) {
                toast(
                  "error",
                  "Please enter a valid number of shares to allot."
                );
                return;
              }

              // In changes list mode, use currently allotted shares + remainder as the maximum limit
              const maxAllowed = isInChangesList
                ? (editableShareAlloc?.allottedShares || 0) +
                  (selectedChange?.remainder || 0)
                : remainingBefore || onOffer;

              if (toAllocate > maxAllowed) {
                toast(
                  "error",
                  `Cannot allot ${toAllocate} shares. Maximum available is ${maxAllowed}.`
                );
                return;
              }

              const official = row?.original || {};
              const officialId = official?._id;
              const fullName =
                getFullName(official) ||
                official?.names?.companyName ||
                "Unknown";

              const currentShares = Array.isArray(row?.original?.sharesAllotted)
                ? row.original.sharesAllotted
                    .filter(
                      (share) => share.classOfShares === values.classOfShares
                    )
                    .reduce(
                      (sum, share) => sum + Number(share.allottedShares || 0),
                      0
                    )
                : 0;
              const votingRights = Array.isArray(row?.original?.sharesAllotted)
                ? row.original.sharesAllotted
                    .filter(
                      (share) => share.classOfShares === values.classOfShares
                    )
                    .reduce(
                      (sum, share) => sum + Number(share.votingRights || 0),
                      0
                    )
                : 0;

              const existingAllocations = Array.isArray(
                selectedChange.allocations
              )
                ? [...selectedChange.allocations]
                : [];

              const idx = existingAllocations.findIndex(
                (a) => String(a?.officialId) === String(officialId)
              );

              const proportionDenominator =
                Number(selectedChange?.sharesOnOffer || 0) || 0;

              const extraAllocationFields = {
                paidUpShares: Number(values.paidUpShares || 0),
                sharePrice: Number(values.sharePrice || 0),
                sharePremium: Number(values.sharePremium || 0),
              };

              let newAllocation;
              if (idx >= 0) {
                const prev = existingAllocations[idx] || {};
                const prevAllocated = Number(prev?.allocatedShares || 0);
                const nextAllocated = editableShareAlloc
                  ? toAllocate // Replace when editing
                  : prevAllocated + toAllocate; // Add when not editing

                let nextProportion = prev?.proportion || "0.000000";
                if (proportionDenominator > 0) {
                  nextProportion = (nextAllocated / proportionDenominator)
                    .toFixed(6)
                    .toString();
                }

                newAllocation = {
                  ...prev,
                  officialId,
                  name: prev?.name || fullName,
                  currentShares,
                  votingRights,
                  allocatedShares: nextAllocated,
                  proportion: nextProportion,
                  ...extraAllocationFields,
                };
                existingAllocations[idx] = newAllocation;
              } else {
                let proportion = "0.000000";
                if (proportionDenominator > 0) {
                  proportion = (toAllocate / proportionDenominator)
                    .toFixed(6)
                    .toString();
                }

                newAllocation = {
                  officialId,
                  name: fullName,
                  currentShares,
                  votingRights,
                  allocatedShares: toAllocate,
                  proportion,
                  ...extraAllocationFields,
                };
                existingAllocations.push(newAllocation);
              }

              const newAllocatedTotal = existingAllocations.reduce(
                (sum, a) => sum + Number(a.allocatedShares || 0),
                0
              );
              const updatedRemainder = Math.max(0, onOffer - newAllocatedTotal);

              const updatedShareCapitalChanges = (
                manualTx.shareCapitalChanges || []
              ).map((chg) =>
                chg._id === selectedChange._id
                  ? {
                      ...chg,
                      allocations: existingAllocations,
                      remainder: updatedRemainder,
                    }
                  : chg
              );

              // Save to Redux instead of updating transaction immediately
              const allotmentData = {
                shareCapitalChanges: updatedShareCapitalChanges,
                allocations: existingAllocations,
                remainder: updatedRemainder,
                classOfShares: values.classOfShares,
                allottedShares: toAllocate,
                paidUpShares: Number(values.paidUpShares || 0),
                sharePrice: Number(values.sharePrice || 0),
                sharePremium: Number(values.sharePremium || 0),
                sharesValue:
                  Number(selectedChange.offerPricePerShare || 0) -
                  Number(selectedChange.premiumPerShare || 0),
                effectiveDate: values.effectiveDate || null,
              };

              saveAllotmentToRedux(allotmentData);

              // Update share allocation table data
              const currentSharesAllotted =
                officialFormContext.values.sharesAllotted || [];
              const newShareEntry = {
                id:
                  editableShareAlloc?.id ||
                  `${selectedChange._id}-${officialId}`,
                classOfShares: values.classOfShares,
                allottedShares: toAllocate,
                sharesValue:
                  Number(selectedChange.offerPricePerShare || 0) -
                  Number(selectedChange.premiumPerShare || 0),
                paidUpShares: Number(values.paidUpShares || 0),
                sharePremium: Number(values.sharePremium || 0),
                nominalValue:
                  toAllocate *
                  (Number(selectedChange.offerPricePerShare || 0) -
                    Number(selectedChange.premiumPerShare || 0)),
                votingRights:
                  toAllocate *
                  (nominalShareCapital.find(
                    (ns) => ns.sharesCategory === values.classOfShares
                  )?.votesPerShare || 0),
              };

              let updatedSharesAllotted;
              if (editableShareAlloc?.id) {
                // Update existing entry
                updatedSharesAllotted = currentSharesAllotted.map((share) =>
                  share.id === editableShareAlloc.id ? newShareEntry : share
                );
              } else {
                // Add new entry
                updatedSharesAllotted = [
                  ...currentSharesAllotted,
                  newShareEntry,
                ];
              }

              officialFormContext.setFieldValue(
                "sharesAllotted",
                updatedSharesAllotted
              );

              // Note: Do NOT update beneficial ownership form here.
              // The max % calculations are handled by the BeneficialOwnershipComponent's useEffect
              // which will automatically update maxPercentShareholding and maxPercentVotingRights
              // based on the saved allocation data, while preserving other BO fields.

              toast(
                "success",
                `Allocated ${toAllocate} ${values.classOfShares} share(s) to ${fullName}.`
              );

              // Reset form and show table
              resetForm();
              setAllocateSharesBtn(true); // Changed from payload to true to show table instead of form
              setEditableShareAlloc(null);

              if (onSubmissionComplete) {
                onSubmissionComplete(allotmentData);
              }
            } catch (err) {
              toast(
                "error",
                err?.message || "Failed to update allocation. Please try again."
              );
            } finally {
              setSubmitting(false);
            }
            return;
          }

          // Default/non-manual path
          const selectedClassOfShares = nominalShareCapital.find(
            ({ sharesCategory }) => sharesCategory === values.classOfShares
          );
          const nominalValue = values.allottedShares * values.sharesValue;
          const payload = {
            ...values,
            nominalValue,
            votingRights:
              values.allottedShares * selectedClassOfShares.votesPerShare,
          };
          const updatedOfficialData = getUpdatedFormData(
            formFields,
            editableShareAlloc || {},
            payload
          );

          if (editableShareAlloc) {
            dispatch(editShares({ ...payload, id: editableShareAlloc.id }));
          } else {
            dispatch(addShares(payload));
          }

          // Reset form and notify parent
          resetForm();

          if (onSubmissionComplete) {
            onSubmissionComplete(payload);
          }
        }}
      >
        {({ setFieldValue, values, handleSubmit }) => (
          <ShareAllocationForm
            formikHelpers={{ setFieldValue, values, handleSubmit }}
            nominalShareCapital={nominalShareCapital}
            isInManualUpdate={isInManualUpdate}
            getManualChangeByClass={getManualChangeByClass}
            computeRemainingForChange={computeRemainingForChange}
            takenShares={takenShares}
            alreadyTakenClassOfShares={alreadyTakenClassOfShares}
            editableShareAlloc={editableShareAlloc}
            setValidationSchema={setValidationSchema}
            isInChangesList={isInChangesList}
            manualClassOptions={manualClassOptions}
            setSelectedClassOfShare={setSelectedClassOfShare}
            selectedClassOfShare={selectedClassOfShare}
            roundNumber={roundNumber}
            handleCancel={handleCancel}
            remainderAmounts={remainderAmounts}
          />
        )}
      </Formik>
    </>
  );
};

export default ShareAllocationSelection;
